import React from "react";
import logger from "../../utils/logger";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    const isChunkError = 
      error?.name === "ChunkLoadError" || 
      /Loading chunk [\d]+ failed/i.test(error?.message || "") ||
      /Failed to fetch dynamically imported module/i.test(error?.message || "");

    return { hasError: true, isChunkError, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error("React ErrorBoundary caught an unhandled component error", {
      error: error?.message || error,
      componentStack: errorInfo?.componentStack
    });

    // Safely retry ONCE for ChunkLoadError (e.g. after a new release) without infinite loops
    if (this.state.isChunkError && typeof window !== "undefined") {
      const hasRetried = sessionStorage.getItem("chunk_retry_attempted");
      if (!hasRetried) {
        sessionStorage.setItem("chunk_retry_attempted", "true");
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    try {
      sessionStorage.removeItem("chunk_retry_attempted");
    } catch {}
    window.location.reload();
  };

  handleGoHome = () => {
    try {
      sessionStorage.removeItem("chunk_retry_attempted");
    } catch {}
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen min-h-[100dvh] flex items-center justify-center bg-[#121212] text-white p-6">
          <div className="max-w-md w-full bg-[#1c1917] border border-stone-700/80 rounded-2xl p-8 text-center shadow-2xl">
            <div className="w-16 h-16 bg-yellow-500/10 text-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold border border-yellow-500/20">
              {this.state.isChunkError ? "🔄" : "⚠️"}
            </div>
            <h2 className="text-xl font-bold text-stone-100 mb-2">
              {this.state.isChunkError ? "App Updated" : "Something went wrong"}
            </h2>
            <p className="text-stone-400 text-sm mb-6">
              {this.state.isChunkError
                ? "A new version of KnightSteps was deployed. Tap reload to get the latest update."
                : "An unexpected error occurred while loading this view."}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="px-5 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-bold rounded-xl transition cursor-pointer"
              >
                Reload
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-5 py-2.5 bg-stone-800 hover:bg-stone-700 text-stone-300 text-sm font-semibold rounded-xl transition cursor-pointer"
              >
                Go to Home
              </button>
            </div>
            {this.state.error && !this.state.isChunkError && (
              <details className="mt-6 text-left border-t border-stone-800 pt-4">
                <summary className="text-xs text-stone-500 hover:text-stone-300 cursor-pointer font-mono select-none">
                  Diagnostic Details
                </summary>
                <div className="mt-2 p-3 bg-black/60 rounded-lg border border-white/5 font-mono text-[11px] text-rose-400/90 overflow-auto max-h-48 whitespace-pre-wrap break-all">
                  <p className="font-bold text-rose-300 mb-1">{String(this.state.error?.message || this.state.error)}</p>
                  {this.state.error?.stack && (
                    <pre className="text-[10px] text-stone-400 leading-tight overflow-x-auto">{this.state.error.stack}</pre>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
